import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { PageHeader } from "../components/ui/page-header";
import {
  ArrowLeft,
  Calendar,
  User,
  FileText,
  Tag,
  Eye,
  Download,
  Edit,
  Trash2,
  Database,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import JSZip from "jszip";
import {
  useDatasetByIdQuery,
  getDatasetById,
  useDeleteDataset,
} from "../api/createDatasetApi";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import WarningDialog from "../components/ui/WarningDialog";
import paths from "../routes";
import { format, parseISO } from "date-fns";
import AddNewDatasetPage from "./AddNewDatasetPage";
import * as XLSX from "xlsx";
import Frame from "../assets/Frame.png";

type FileContent = {
  loading: boolean;
  error?: string;
  text?: string;
  headers?: string[];
  rows?: string[][];
  json?: any;
};

const ViewDatasetPage: React.FC = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Record<string, boolean>>(
    {},
  );
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get("returnTo") || (location.state as any)?.returnTo;
  const returnUrl = searchParams.get("returnUrl") || (location.state as any)?.returnUrl;

  const handleEditClick = async () => {
    try {
      // Call the API to fetch the latest dataset data
      await getDatasetById(datasetId || "");
      // Open the edit modal
      setIsEditModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch dataset for editing:", error);
      // Still open the modal with existing data if API fails
      setIsEditModalOpen(true);
    }
  };

  const { mutate: deleteDatasetMutation, isPending: isDeleting } =
    useDeleteDataset();

  const handleDelete = () => {
    if (dataset?.projects?.length && dataset.projects.length > 0) {
      const projectsList = dataset.projects.map(
        (p: any) => p.title || p.name || p.id || "Unnamed Project",
      );
      setLinkedProjects(projectsList);
      setShowProjectsWarning(true);
    } else {
      setConfirmOpen(true);
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | undefined>(
    undefined,
  );
  const [showProjectsWarning, setShowProjectsWarning] = useState(false);
  const [linkedProjects, setLinkedProjects] = useState<string[]>([]);

  const onConfirmDelete = () => {
    setConfirmOpen(false);
    if (!dataset?.id) return;
    deleteDatasetMutation(dataset.id, {
      onSuccess: () => {
        toast.success("Dataset deleted successfully");
        navigate(paths.dataSet);
      },
      onError: (error: any) => {
        const message = error?.response?.data?.data?.message || "Delete failed";
        setWarningMessage(message);
        setWarningOpen(true);
      },
    });
  };

  // Fetch the specific dataset by ID
  const {
    data: dataset,
    isLoading,
    isError,
    refetch,
  } = useDatasetByIdQuery(datasetId || "");

  const handleBackNavigation = () => {
    if (returnTo === "project-creation" && returnUrl) {
      navigate(returnUrl); // Navigate to specific project creation URL
    } else {
      navigate(paths.dataSet); // Go to datasets list
    }
  };

  const handleDownloadAll = async () => {
    if (!dataset?.files || dataset.files.length === 0) return;

    const guessNameFromFile = (file: any) => {
      return (
        file?.originalName ||
        file?.file_details?.file_name ||
        file?.key ||
        "file"
      );
    };

    const mapContentTypeToExt = (ct: string | null) => {
      if (!ct) return "";
      if (ct.includes("csv")) return ".csv";
      if (ct.includes("json")) return ".json";
      if (ct.includes("excel") || ct.includes("spreadsheet")) return ".xlsx";
      if (ct.includes("text")) return ".txt";
      if (ct.includes("pdf")) return ".pdf";
      return "";
    };

    const downloadUrlAsFile = async (url: string, preferredName?: string) => {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
      const cd = res.headers.get("content-disposition");
      let filename =
        extractFilenameFromContentDisposition(cd) || preferredName || "file";
      // if no extension, try to infer from URL path
      const hasExt = filename && filename.includes(".");
      if (!hasExt) {
        try {
          const path = new URL(url).pathname;
          const part = path.split("/").pop() || "";
          if (part.includes("."))
            filename = filename + "." + part.split(".").pop();
          else {
            const ct = res.headers.get("content-type");
            const ext = mapContentTypeToExt(ct);
            if (ext) filename = filename + ext;
          }
        } catch (e) {
          const ct = res.headers.get("content-type");
          const ext = mapContentTypeToExt(ct);
          if (ext) filename = filename + ext;
        }
      }

      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    };

    // If only one file, download it directly via blob (to preserve name)
    if (dataset.files.length === 1) {
      const file = dataset.files[0];
      const url = file?.download_url;
      if (!url) return;
      const preferred = guessNameFromFile(file);
      try {
        await downloadUrlAsFile(url, preferred);
      } catch (err) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      return;
    }

    // Multiple files: try to fetch each file as blob and zip them
    try {
      const zip = new JSZip();

      const fetchers = (dataset.files || []).map(async (file: any) => {
        const url = file?.download_url;
        if (!url) return;
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) {
          throw new Error(`Failed to fetch ${url}: ${res.status}`);
        }
        const blob = await res.blob();
        const name =
          file?.originalName ||
          file?.file_details?.file_name ||
          url.split("/").pop() ||
          "file";
        zip.file(name, blob);
      });

      await Promise.all(fetchers);

      const content = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(content);
      const safeTitle = (dataset.title || "dataset").replace(
        /[^a-z0-9-_\.]/gi,
        "-",
      );
      a.download = `${safeTitle}-files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // release the object URL after a short delay
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch (error) {
      console.error(
        "Zip download failed, falling back to individual downloads:",
        error,
      );
      // Fallback: trigger individual downloads via blob
      for (const file of dataset.files || []) {
        const url = file?.download_url;
        if (!url) continue;
        const preferred = guessNameFromFile(file);
        try {
          // attempt blob download to preserve filename
          // eslint-disable-next-line no-await-in-loop
          await downloadUrlAsFile(url, preferred);
        } catch (e) {
          // final fallback: open in new tab
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      }
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const guessNameFromFile = (file: any) => {
    return (
      file?.originalName || file?.file_details?.file_name || file?.key || "file"
    );
  };

  const extractFilenameFromContentDisposition = (cd: string | null) => {
    if (!cd) return null;
    const fnStar = /filename\*=(?:[^']*'[^']*')?([^;\n]+)/i.exec(cd);
    if (fnStar && fnStar[1]) return fnStar[1].trim().replace(/['"]/g, "");
    const fn = /filename=(?:"([^"]+)"|([^;\n]+))/i.exec(cd);
    if (fn && (fn[1] || fn[2])) {
      const filename = fn[1] || fn[2];
      if (filename) return filename.trim().replace(/['"]/g, "");
    }
    return null;
  };

  const mapContentTypeToExt = (ct: string | null) => {
    if (!ct) return "";
    if (ct.includes("csv")) return ".csv";
    if (ct.includes("json")) return ".json";
    if (ct.includes("excel") || ct.includes("spreadsheet")) return ".xlsx";
    if (ct.includes("text")) return ".txt";
    if (ct.includes("pdf")) return ".pdf";
    return "";
  };

  const downloadUrlAsFile = async (url: string, preferredName?: string) => {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    const cd = res.headers.get("content-disposition");
    // Prefer the caller-provided preferredName (original filename) over
    // server-provided Content-Disposition. Sanitize to avoid path parts
    // or surrounding quotes.
    let filename =
      preferredName || extractFilenameFromContentDisposition(cd) || "file";
    filename = String(filename)
      .trim()
      .replace(/^[\"']|[\"']$/g, "");
    // If preferredName included a path, take only the last segment
    if (filename.includes("/"))
      filename = filename.split("/").pop() || filename;
    const hasExt = filename && filename.includes(".");
    if (!hasExt) {
      try {
        const path = new URL(url).pathname;
        const part = path.split("/").pop() || "";
        if (part.includes("."))
          filename = filename + "." + part.split(".").pop();
        else {
          const ct = res.headers.get("content-type");
          const ext = mapContentTypeToExt(ct);
          if (ext) filename = filename + ext;
        }
      } catch (e) {
        const ct = res.headers.get("content-type");
        const ext = mapContentTypeToExt(ct);
        if (ext) filename = filename + ext;
      }
    }

    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
  };

  const downloadFile = async (file: any) => {
    const url = file?.download_url;
    if (!url) return;
    const preferred = guessNameFromFile(file);
    try {
      await downloadUrlAsFile(url, preferred);
    } catch (err) {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const [fileContents, setFileContents] = useState<Record<string, FileContent>>(
    {},
  );

  const parseCSV = (text: string) => {
    const rows: string[][] = [];
    let cur = "";
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') {
        if (inQuotes && text[i + 1] === '"') {
          cur += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (!inQuotes && (ch === "," || ch === "\n" || ch === "\r")) {
        if (ch === ",") {
          row.push(cur);
          cur = "";
        } else if (ch === "\n") {
          row.push(cur);
          rows.push(row);
          row = [];
          cur = "";
        }
        // ignore \r explicitly
        continue;
      }
      cur += ch;
    }
    if (cur !== "" || row.length > 0) {
      row.push(cur);
      rows.push(row);
    }
    const headers = rows.length > 0 ? rows[0] : undefined;
    const body = rows.length > 1 ? rows.slice(1) : [];
    return { headers, rows: body };
  };

  const loadFileContent = async (id: string, url?: string) => {
    if (!url) {
      setFileContents((prev) => ({
        ...prev,
        [id]: { loading: false, error: "No download URL" },
      }));
      return;
    }

    const existing = fileContents[id];
    if (
      existing &&
      (existing.loading || existing.rows || existing.json || existing.text)
    ) {
      return;
    }

    setFileContents((prev) => ({ ...prev, [id]: { loading: true } }));

    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

      // Determine content type and filename (from headers or URL)
      const contentType = (res.headers.get("content-type") || "").toLowerCase();
      const contentDisposition = res.headers.get("content-disposition");
      const cdFilename =
        extractFilenameFromContentDisposition(contentDisposition);
      const urlLower = (url || "").toLowerCase();
      const guessedFilename = cdFilename || urlLower.split("/").pop() || "";

      const isExcel =
        urlLower.endsWith(".xlsx") ||
        urlLower.endsWith(".xls") ||
        contentType.includes("spreadsheet") ||
        contentType.includes("excel") ||
        contentType.includes("vnd.openxmlformats") ||
        contentType.includes("officedocument") ||
        /\.(xlsx|xls)$/i.test(guessedFilename);

      /* ✅ XLSX handling (detect by header or filename, not just URL suffix) */
      if (isExcel) {
        const arrayBuffer = await res.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        if (!sheetName || typeof sheetName !== "string")
          throw new Error("No sheets found in Excel file");
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) throw new Error("Sheet not found in Excel file");

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1, // array of arrays
          blankrows: false,
        }) as string[][];

        const headers = jsonData[0] || [];
        const rows = jsonData.slice(1);

        setFileContents((prev) => ({
          ...prev,
          [id]: { loading: false, headers, rows },
        }));
        return;
      }

      /* ✅ JSON */
      const text = await res.text();
      try {
        const parsed = JSON.parse(text);
        setFileContents((prev) => ({
          ...prev,
          [id]: { loading: false, json: parsed },
        }));
        return;
      } catch {}

      /* ✅ CSV */
      if (text.includes("\n") && text.includes(",")) {
        const { headers, rows } = parseCSV(text);
        const update: any = { loading: false, rows };
        if (headers) update.headers = headers;
        setFileContents((prev) => ({
          ...prev,
          [id]: update,
        }));
        return;
      }

      /* ✅ Plain text */
      setFileContents((prev) => ({
        ...prev,
        [id]: { loading: false, text },
      }));
    } catch (error: any) {
      setFileContents((prev) => ({
        ...prev,
        [id]: { loading: false, error: error.message || String(error) },
      }));
    }
  };
  const SidebarItem = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div>
      <p className="text-gray-500">{label}</p>
      <p className="font-medium text-gray-900 mt-1">
        {value !== undefined ? value : ""}
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6 bg-white min-h-screen">
        <div className="text-center py-12">
          <p>Loading dataset details...</p>
        </div>
      </div>
    );
  }

  if (isError || !dataset) {
    return (
      <div className="p-6 bg-white min-h-screen">
        <div className="text-center py-12">
          <p>Failed to load dataset details or dataset not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      <PageHeader title={"Dataset Details"} subtitle="">
        <Button
          variant="outline"
          onClick={handleBackNavigation}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {returnTo === "project-creation"
            ? "back to edit project"
            : "Back to Datasets"}
        </Button>
      </PageHeader>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Cover Banner */}
        <div className="w-full h-56 bg-gray-100 rounded-xl overflow-hidden mb-6">
          {(dataset as any).cover_photo_url ? (
            <img
              src={(dataset as any).cover_photo_url}
              alt={dataset.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={Frame}
              alt="frame"
              className="w-full h-full object-cover"
            />
          )}
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {dataset?.title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              By {(dataset as any).uploader?.name || "Unknown"} • Updated on{" "}
              {(dataset as any).updated_at
                ? format(parseISO(dataset.updated_at), "dd/MM/yyyy")
                : "N/A"}
            </p>
          </div>

          <div className="flex gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleEditClick}>
                  <Edit className="w-4 h-4 text-[#2076D1]" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit dataset</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  <Trash2 className="w-4 h-4 text-[#D92D20]" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete dataset</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8">
          <div className="space-y-6">
            {/* Description */}
            <div>
              <h2 className="text-sm font-bold text-gray-700 mb-2">
                Description
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                <div
                  className="ml-8 mr-4 mb-4 text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{
                    __html: dataset?.description || "No description available.",
                  }}
                />
                {/* {dataset?.description || ""} */}
              </p>
            </div>

            {/* Files */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-bold text-gray-700">Files</h2>
                <Button
                  variant="link"
                  size="sm"
                  text-color="#2076D1"
                  className="border-0"
                  onClick={handleDownloadAll}
                >
                  <Download className="w-4 h-4 mr-1 text-[#2076D1]" />
                  <div className="text-[#2076D1]">Download all</div>
                </Button>
              </div>

              <div className="border  divide-y">
                {dataset.files && dataset.files.length > 0 ? (
                  dataset.files.map((file: any, idx: number) => {
                    const id = file?.key || file?.originalName || String(idx);
                    return (
                      <div key={id} className="">
                        <div className="group flex items-center gap-3 px-4 py-3 text-sm border-0 hover:bg-gray-50">
                          <Database className="w-4 h-4 text-gray-500" />
                          <div className="flex-1 min-w-0 ">
                            <span className="block break-words whitespace-normal">
                              {file?.originalName || file.key}
                            </span>
                            <span className="text-gray-400 text-xs">
                              {formatSize(file.file_details?.file_size)}
                            </span>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadFile(file)}
                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150"
                          >
                            <Download className="w-4 h-4" />
                          </Button>

                          {/* Expand/collapse toggle */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const willOpen = !expandedFiles[id];
                              setExpandedFiles((prev) => ({
                                ...prev,
                                [id]: willOpen,
                              }));
                              if (willOpen)
                                loadFileContent(id, file.download_url);
                            }}
                            className="focus:opacity-100 transition-opacity duration-150"
                            aria-label="Toggle details"
                          >
                            {expandedFiles[id] ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        </div>

                        {/* Collapsible area: file content (CSV/JSON/plain) */}
                        {expandedFiles[id] && (
                          <div className="px-10 py-2 text-sm text-gray-600 bg-gray-50">
                            <div className="overflow-x-auto">
                              {(() => {
                                const content = fileContents[id] || {
                                  loading: false,
                                };
                                if (content.loading) {
                                  return (
                                    <div className="py-4">
                                      Loading file content...
                                    </div>
                                  );
                                }
                                if (content.error) {
                                  return (
                                    <div className="py-2 text-red-600">
                                      {content.error}
                                    </div>
                                  );
                                }
                                if (content.rows) {
                                  const headers =
                                    content.headers ||
                                    (content.rows[0] || []).map(
                                      (_, i) => `col${i + 1}`,
                                    );
                                  const rows = content.rows;
                                  const maxCols = Math.min(
                                    50,
                                    Math.max(
                                      headers.length,
                                      ...rows.map((r) => r.length),
                                    ),
                                  );
                                  const visibleCols = headers.slice(0, maxCols);
                                  return (
                                    <table className="w-full text-left text-sm border">
                                      <thead>
                                        <tr className="bg-gray-100 text-gray-700">
                                          {visibleCols.map((h, i) => (
                                            <th
                                              key={i}
                                              className="px-2 py-1 border"
                                            >
                                              {h || `col${i + 1}`}
                                            </th>
                                          ))}
                                          {maxCols < headers.length && (
                                            <th className="px-2 py-1 border">
                                              …
                                            </th>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rows.slice(0, 200).map((r, ri) => (
                                          <tr
                                            key={ri}
                                            className={
                                              ri % 2 === 0
                                                ? "bg-white"
                                                : "bg-gray-50"
                                            }
                                          >
                                            {Array.from({
                                              length: visibleCols.length,
                                            }).map((_, ci) => (
                                              <td
                                                key={ci}
                                                className="px-2 py-1 border break-words"
                                              >
                                                {r[ci] ?? ""}
                                              </td>
                                            ))}
                                            {maxCols < r.length && (
                                              <td className="px-2 py-1 border">
                                                …
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  );
                                }
                                if (content.json) {
                                  const json = content.json;
                                  if (
                                    Array.isArray(json) &&
                                    json.length > 0 &&
                                    typeof json[0] === "object"
                                  ) {
                                    const rows = json as Record<string, any>[];
                                    const cols = Array.from(
                                      new Set(
                                        rows.flatMap((r) => Object.keys(r)),
                                      ),
                                    );
                                    const visibleCols = cols.slice(0, 50);
                                    return (
                                      <table className="w-full text-left text-sm border">
                                        <thead>
                                          <tr className="bg-gray-100 text-gray-700">
                                            {visibleCols.map((c) => (
                                              <th
                                                key={c}
                                                className="px-2 py-1 border"
                                              >
                                                {c}
                                              </th>
                                            ))}
                                            {cols.length >
                                              visibleCols.length && (
                                              <th className="px-2 py-1 border">
                                                …
                                              </th>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {rows.slice(0, 200).map((r, ri) => (
                                            <tr
                                              key={ri}
                                              className={
                                                ri % 2 === 0
                                                  ? "bg-white"
                                                  : "bg-gray-50"
                                              }
                                            >
                                              {visibleCols.map((c, ci) => (
                                                <td
                                                  key={ci}
                                                  className="px-2 py-1 border break-words"
                                                >
                                                  {String(r[c] ?? "")}
                                                </td>
                                              ))}
                                              {cols.length >
                                                visibleCols.length && (
                                                <td className="px-2 py-1 border">
                                                  …
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    );
                                  }
                                  return (
                                    <pre className="whitespace-pre-wrap">
                                      {JSON.stringify(json, null, 2)}
                                    </pre>
                                  );
                                }
                                if (content.text) {
                                  return (
                                    <pre className="whitespace-pre-wrap">
                                      {content.text}
                                    </pre>
                                  );
                                }
                                return (
                                  <div className="py-2">
                                    No preview available.
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    No files available
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4 text-sm">
            {/* <SidebarItem
              label="Version"
              value={(dataset as any).version || "-"}
            /> */}
            <SidebarItem
              label="Projects associated"
              value={
                (dataset as any).projects?.length > 0 ? (
                  <div>
                    <div className="font-medium">
                      {(dataset as any).projects.length}
                    </div>
                    {/* {(dataset as any).projects
                      .slice(0, 3)
                      .map((p: any, i: number) => (
                        <div key={i} className="text-xs text-gray-500">
                          {p.title || p.name || `Project ${p.id?.slice(-6)}`}
                        </div>
                      ))} */}
                    {/* {(dataset as any).projects.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{(dataset as any).projects.length - 3} more
                      </div>
                    )} */}
                  </div>
                ) : (
                  0
                )
              }
            />

            <SidebarItem
              label="Created on"
              value={
                (dataset as any).created_at
                  ? format(parseISO(dataset.created_at), "dd/MM/yyyy")
                  : "—"
              }
            />
            <SidebarItem
              label="Updated on"
              value={
                (dataset as any).updated_at
                  ? format(parseISO(dataset.updated_at), "dd/MM/yyyy")
                  : "—"
              }
            />
            <SidebarItem
              label="Visibility"
              value={(dataset as any).visibility || "—"}
            />

            {/* Tags */}
            {(dataset as any).tags?.length > 0 && (
              <div>
                <p className="text-gray-500 mb-2">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {(dataset as any).tags.map((tag: string) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="rounded-none bg-transparent border border-gray-300 text-gray-700"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Context */}
            {(dataset as any).context && (
              <SidebarItem label="Context" value={(dataset as any).context} />
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <AddNewDatasetPage
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            refetch && refetch();
            setIsEditModalOpen(false);
          }}
          dataset={dataset}
        />
      )}
      <ConfirmDialog
        open={confirmOpen}
        title={`Delete dataset?`}
        description={
          <span>
            Are you sure you want to delete <strong>{dataset?.title}</strong>?{" "}
            {dataset.projects && dataset.projects.length > 0 && (
              <span className="text-orange-600">
                (This will also unlink it from {dataset.projects.length}{" "}
                associated project{dataset.projects.length !== 1 ? "s" : ""}.)
              </span>
            )}
          </span>
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      <WarningDialog
        open={warningOpen}
        title="Error"
        message={warningMessage}
        onClose={() => setWarningOpen(false)}
      />

      <WarningDialog
        open={showProjectsWarning}
        title="Dataset is linked to projects"
        message={
          <div>
            <p>This dataset is used by the following project(s):</p>
            <ul className="mt-2 list-disc pl-5">
              {linkedProjects.map((proj, idx) => (
                <li key={idx} className="text-sm">
                  {proj}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-gray-600">
              Deleting this dataset may affect these projects. Unlink them then
              proceed with deletion.
            </p>
          </div>
        }
        onClose={() => {
          setShowProjectsWarning(false);
          setConfirmOpen(true);
        }}
      />
    </div>
  );
};

export default ViewDatasetPage;
